'use client';
import Link from 'next/link';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white text-[#0f172a]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <Link href="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#1a56ff] mb-8 transition-colors">
          <ArrowLeft size={16} /> Back to Home
        </Link>
        <h1 className="text-3xl sm:text-4xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-xs text-slate-400 mb-2">Last updated: May 2026</p>
        <p className="text-sm text-slate-500 mb-10">
          DropService Digital Services ("we", "us", or "our") is committed to protecting your personal information. This Privacy Policy explains what data we collect, how we use it, and your rights — in compliance with the Information Technology Act, 2000 and the Digital Personal Data Protection Act, 2023 (India).
        </p>

        <div className="space-y-8 text-sm text-slate-600 leading-relaxed">

          <section>
            <h2 className="text-base font-bold text-[#0f172a] mb-3">1. Information We Collect</h2>
            <p className="mb-2">We collect the following categories of personal data:</p>
            <div className="space-y-3">
              <div>
                <p className="font-semibold text-[#0f172a]">a) Identity & Contact Information</p>
                <ul className="list-disc pl-5 mt-1 space-y-1">
                  <li>Full legal name (as per Aadhaar card)</li>
                  <li>Email address and mobile number</li>
                  <li>Instagram handle (optional, for marketing purposes)</li>
                  <li>Profile photograph (if uploaded)</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-[#0f172a]">b) Financial & Transaction Data</p>
                <ul className="list-disc pl-5 mt-1 space-y-1">
                  <li>Payment transaction IDs and amounts</li>
                  <li>Wallet balance and transaction history</li>
                  <li>Commission and referral earnings records</li>
                  <li>Order history and service purchase records</li>
                </ul>
                <p className="mt-1 text-xs text-slate-500">Note: We do not store full card numbers, CVV codes, or bank account credentials. All sensitive payment data is processed and stored by Cashfree Payments.</p>
              </div>
              <div>
                <p className="font-semibold text-[#0f172a]">c) KYC & Verification Data</p>
                <ul className="list-disc pl-5 mt-1 space-y-1">
                  <li>Government-issued ID details (Aadhaar, PAN) — collected only when required for compliance or high-value transactions</li>
                  <li>KYC verification status</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-[#0f172a]">d) Technical & Usage Data</p>
                <ul className="list-disc pl-5 mt-1 space-y-1">
                  <li>IP address, browser type, and device information</li>
                  <li>Pages visited, features used, and time spent on the Platform</li>
                  <li>Login timestamps and session data</li>
                  <li>Referral codes used and affiliate link clicks</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-base font-bold text-[#0f172a] mb-3">2. How We Use Your Information</h2>
            <p className="mb-2">We use your personal data for the following purposes:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Account Management:</strong> Creating and maintaining your agent account, verifying your identity, and providing access to the Platform.</li>
              <li><strong>Payment Processing:</strong> Facilitating wallet top-ups, order payments, and commission credits via Cashfree Payments.</li>
              <li><strong>Order Fulfilment:</strong> Processing your service orders and communicating order status updates.</li>
              <li><strong>Affiliate Programme:</strong> Tracking referrals, calculating commissions, and crediting earnings to your wallet.</li>
              <li><strong>Communications:</strong> Sending transactional emails (order confirmations, payment receipts, password resets), platform announcements, and support responses.</li>
              <li><strong>Fraud Prevention & Compliance:</strong> Detecting and preventing fraudulent activity, money laundering, and other prohibited conduct; complying with applicable Indian laws and RBI guidelines.</li>
              <li><strong>Platform Improvement:</strong> Analysing usage patterns to improve features, fix bugs, and enhance user experience.</li>
              <li><strong>Legal Obligations:</strong> Retaining records as required by Indian tax law (GST), the DPDP Act, and other applicable regulations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-bold text-[#0f172a] mb-3">3. Legal Basis for Processing</h2>
            <p className="mb-2">We process your personal data on the following legal bases under the DPDP Act, 2023:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Consent:</strong> You provide explicit consent during registration by accepting these Terms and our Privacy Policy.</li>
              <li><strong>Contractual Necessity:</strong> Processing required to fulfil our service agreement with you (account management, order processing, payments).</li>
              <li><strong>Legal Obligation:</strong> Processing required to comply with Indian laws (GST filings, KYC requirements, RBI guidelines).</li>
              <li><strong>Legitimate Interests:</strong> Fraud prevention, platform security, and improving our services.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-bold text-[#0f172a] mb-3">4. Payment Data & Third-Party Processors</h2>
            <p className="mb-2">All payment transactions are processed by <strong>Cashfree Payments</strong> (Cashfree Payments India Pvt. Ltd.), a PCI-DSS compliant payment aggregator authorised by the Reserve Bank of India. When you make a payment:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>You are redirected to or interact with Cashfree's secure payment interface.</li>
              <li>Cashfree collects and processes your card/UPI/net banking details directly.</li>
              <li>We receive only a transaction reference ID and payment status — never your full card or bank credentials.</li>
              <li>Cashfree's privacy policy governs their handling of your payment data: <a href="https://www.cashfree.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#1a56ff] hover:underline">cashfree.com/privacy-policy</a></li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-bold text-[#0f172a] mb-3">5. Data Sharing & Disclosure</h2>
            <p className="mb-2">We do not sell your personal data. We may share your data with:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Payment Processors:</strong> Cashfree Payments, for transaction processing.</li>
              <li><strong>Email Service Providers:</strong> Resend (for transactional emails). They process your email address only to deliver messages on our behalf.</li>
              <li><strong>Cloud Infrastructure:</strong> Supabase (database and authentication), hosted on AWS infrastructure in India/Singapore.</li>
              <li><strong>Analytics Providers:</strong> Anonymised usage data may be shared with analytics tools to improve the Platform.</li>
              <li><strong>Law Enforcement & Regulators:</strong> We may disclose your data if required by law, court order, or government authority, including the Income Tax Department, GST authorities, or RBI.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your data may be transferred to the successor entity.</li>
            </ul>
            <p className="mt-2">All third-party service providers are contractually bound to process your data only as instructed by us and to maintain appropriate security standards.</p>
          </section>

          <section>
            <h2 className="text-base font-bold text-[#0f172a] mb-3">6. Data Retention</h2>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Active Accounts:</strong> We retain your data for as long as your account is active.</li>
              <li><strong>Financial Records:</strong> Transaction records, invoices, and wallet history are retained for a minimum of <strong>7 years</strong> as required by Indian tax law (GST Act, Income Tax Act).</li>
              <li><strong>Closed Accounts:</strong> Upon account closure, non-financial personal data is deleted or anonymised within 90 days, subject to legal retention requirements.</li>
              <li><strong>KYC Data:</strong> Retained for the period required by applicable KYC/AML regulations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-bold text-[#0f172a] mb-3">7. Data Security</h2>
            <p className="mb-2">We implement industry-standard technical and organisational measures to protect your personal data:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>All data is transmitted over HTTPS/TLS encryption.</li>
              <li>Passwords are hashed using bcrypt and never stored in plain text.</li>
              <li>Database access is restricted using Row-Level Security (RLS) policies.</li>
              <li>Regular security audits and vulnerability assessments are conducted.</li>
              <li>Access to personal data is limited to authorised personnel on a need-to-know basis.</li>
            </ul>
            <p className="mt-2">Despite these measures, no method of transmission over the internet is 100% secure. In the event of a data breach affecting your rights, we will notify you within 72 hours as required by applicable law.</p>
          </section>

          <section>
            <h2 className="text-base font-bold text-[#0f172a] mb-3">8. Cookies & Tracking</h2>
            <p className="mb-2">We use cookies and similar technologies to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Maintain your login session (essential cookies — cannot be disabled).</li>
              <li>Remember your preferences and settings.</li>
              <li>Analyse Platform usage through anonymised analytics.</li>
            </ul>
            <p className="mt-2">For full details on the types of cookies we use and how to manage them, see our <Link href="/cookie-policy" className="text-[#1a56ff] hover:underline">Cookie Policy</Link>.</p>
          </section>

          <section>
            <h2 className="text-base font-bold text-[#0f172a] mb-3">9. Your Rights (DPDP Act, 2023)</h2>
            <p className="mb-2">Under the Digital Personal Data Protection Act, 2023, you have the following rights:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Right to Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Right to Correction:</strong> Request correction of inaccurate or incomplete personal data.</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your personal data, subject to legal retention requirements.</li>
              <li><strong>Right to Grievance Redressal:</strong> Lodge a complaint with our Data Protection Officer.</li>
              <li><strong>Right to Nominate:</strong> Nominate another individual to exercise your rights in the event of your death or incapacity.</li>
            </ul>
            <p className="mt-2">To exercise any of these rights, email us at <a href="mailto:privacy@dropservice.in" className="text-[#1a56ff] hover:underline">privacy@dropservice.in</a>. We will respond within 30 days. Note that exercising certain rights (e.g., erasure) may result in account closure, as some data is necessary to provide our services.</p>
          </section>

          <section>
            <h2 className="text-base font-bold text-[#0f172a] mb-3">10. Children's Privacy</h2>
            <p>The Platform is not directed at individuals under the age of 18. We do not knowingly collect personal data from minors. If we become aware that a minor has registered, we will immediately delete their account and associated data.</p>
          </section>

          <section>
            <h2 className="text-base font-bold text-[#0f172a] mb-3">11. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of material changes via email or in-app notification at least 14 days before they take effect. The "Last updated" date at the top of this page reflects the most recent revision. Continued use of the Platform after the effective date constitutes acceptance of the revised Policy.</p>
          </section>

          <section>
            <h2 className="text-base font-bold text-[#0f172a] mb-3">12. Contact & Grievance Officer</h2>
            <p className="mb-2">For privacy-related queries, data requests, or complaints, contact our Data Protection Officer:</p>
            <ul className="list-none space-y-1">
              <li><strong>Name:</strong> Data Protection Officer, DropService Digital Services</li>
              <li><strong>Email:</strong> <a href="mailto:privacy@dropservice.in" className="text-[#1a56ff] hover:underline">privacy@dropservice.in</a></li>
              <li><strong>Support:</strong> <a href="mailto:support@dropservice.in" className="text-[#1a56ff] hover:underline">support@dropservice.in</a></li>
              <li><strong>Address:</strong> DropService Digital Services, Mumbai, Maharashtra, India</li>
            </ul>
            <p className="mt-2">If you are not satisfied with our response, you may lodge a complaint with the Data Protection Board of India once it is constituted under the DPDP Act, 2023.</p>
          </section>

        </div>

        <div className="mt-12 pt-8 border-t border-slate-100 flex flex-wrap gap-4 text-xs text-slate-400">
          <Link href="/terms" className="hover:text-[#1a56ff] transition-colors">Terms of Service</Link>
          <Link href="/refund-policy" className="hover:text-[#1a56ff] transition-colors">Refund Policy</Link>
          <Link href="/cookie-policy" className="hover:text-[#1a56ff] transition-colors">Cookie Policy</Link>
          <Link href="/contact" className="hover:text-[#1a56ff] transition-colors">Contact Us</Link>
        </div>
      </div>
    </div>
  );
}
